import * as Yup from 'yup'

import { BTN_TEXT_ENABLED, BTN_TEXT_PROCESSING } from 'constants.js'
import {
    PaywallDisplay,
    getAlertMsg,
    getAlertState,
    getAlertType,
    getDisplayState,
    getPaymentToken,
    getSubmitBtnState,
    getSubmitBtnTxt,
    paymentTokenReceived,
    purchaseNewPass,
    successfulPurchaseCallback,
    retrievePaymentToken,
    setDisplayState,
    shouldClearSelectedPayment,
    updateAlertAndBtnState,
} from 'redux/slices/paywall'
import React, { Component } from 'react'
import { bindActionCreators, compose } from 'redux'

import Alert from 'react-bootstrap/Alert'
import CallToActionButton from 'components/common/CallToActionButton/CallToActionButton'
import { PaywallContainer } from 'components/common/PaywallContainer/PaywallContainer'
import PaywallEmailInput from './PaywallEmailInput/PaywallEmailInput'
import PropTypes from 'prop-types'
import _ from 'lodash'
import clsx from 'clsx'
import { connect } from 'react-redux'
import { withStyles } from '@material-ui/core'
import dropin from 'braintree-web-drop-in'
import { getChosenPassTier } from 'redux/slices/passTier'
import { getShow } from 'redux/slices/liveShow'
import { isMobile } from 'utils'
import numeral from 'numeral'
import { openModal } from 'redux/slices/globalModal'
import { styles } from './styles'

const validationSchema = Yup.object().shape({
    email: Yup.string()
        .email('Please enter a valid email address')
        .required('Please provide an email address'),
})

const getPaymentCountryCode = payload => {
    switch (payload.type) {
        case 'PayPalAccount':
            return _.get(payload, 'details.billingAddress.countryCode')

        case 'CreditCard':
            return _.get(payload, 'binData.countryOfIssuance')

        case 'VenmoAccount':
            return 'US'

        default:
            return 'Unknown'
    }
}

class Paywall extends Component {
    constructor(props) {
        super(props)
        this.state = {
            paymentInstance: {},
            customFieldsValid: false,
            customFieldEmailValue: '',
        }
    }

    componentDidMount() {
        this.props.retrievePaymentToken()

        if (this.props.displayState === PaywallDisplay.PAYMENT && this.props.paymentToken) {
            this.getPaymentInstance()
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.displayState === PaywallDisplay.ACCESS) {
            //Do not show the paywall since the user already redeemed a pass
            this.props.successfulPurchaseCallback()
        } else if (this.props.paymentToken && prevProps.paymentToken !== this.props.paymentToken) {
            this.getPaymentInstance()
        } else if (this.props.shouldClearSelectedPayment && !prevProps.shouldClearSelectedPayment) {
            //Error response was received from transaction request
            this.state.paymentInstance.clearSelectedPaymentMethod()
        }
    }

    /**
     * Creates a Drop-In UI form via the Braintree SDK and sets a new Braintree payment instance to the state
     */
    getPaymentInstance = async () => {
        try {
            if (!this.props.paymentToken) {
                const alertMsg = 'Error encountered initializing payment form. Please go back and try again.'
                this.props.updateAlertAndBtnState({
                    disableSubmit: true,
                    submitBtnText: BTN_TEXT_ENABLED,
                    showAlert: true,
                    alertMsg,
                    alertType: 'danger',
                })
                return
            }

            let instance = await dropin.create({
                authorization: this.props.paymentToken,
                vaultManager: true,
                container: '#dropin-container',
                dataCollector: {
                    kount: true,
                },
                venmo: {
                    allowNewBrowserTab: true,
                },
                paypal: {
                    flow: 'vault',
                    currency: 'USD',
                },
            })

            //Set event handlers
            instance.on('paymentMethodRequestable', () => {
                const btnTxt = this.props.submitBtnText === BTN_TEXT_PROCESSING ? BTN_TEXT_PROCESSING : BTN_TEXT_ENABLED //This event handler can be called after payment begins processing
                this.props.updateAlertAndBtnState({
                    disableSubmit: this.props.submitBtnText === BTN_TEXT_PROCESSING,
                    submitBtnText: btnTxt,
                })
            })
            instance.on('noPaymentMethodRequestable', () => {
                const btnTxt = this.props.submitBtnText === BTN_TEXT_PROCESSING ? BTN_TEXT_PROCESSING : BTN_TEXT_ENABLED //This event handler can be called after payment begins processing
                this.props.updateAlertAndBtnState({
                    disableSubmit: true,
                    submitBtnText: btnTxt,
                    showAlert: false,
                    alertMsg: '',
                    alertType: 'danger',
                })
            })
            if (instance.isPaymentMethodRequestable()) {
                // This will be true if there is a saved payment method available for this user
                this.props.updateAlertAndBtnState({
                    disableSubmit: false,
                    submitBtnText: BTN_TEXT_ENABLED,
                    showAlert: false,
                    alertMsg: '',
                    alertType: 'danger',
                })
            } else {
                //Drop-in UI has loaded but no payment options are requestable
                this.props.updateAlertAndBtnState({
                    disableSubmit: true,
                    submitBtnText: BTN_TEXT_ENABLED,
                    showAlert: false,
                    alertMsg: '',
                    alertType: 'danger',
                })
            }

            this.setState({
                paymentInstance: instance,
                ...(this.props.isAuthenticated && { customFieldsValid: true }), //Authenticated users do not need to input custom fields
            })
            this.props.displayState !== PaywallDisplay.PAYMENT && this.props.setDisplayState(PaywallDisplay.PAYMENT) //Only update if needed
        } catch (err) {
            const alertMsg = 'Error encountered initializing payment form. Please go back and try again.'
            this.props.updateAlertAndBtnState({
                disableSubmit: true,
                submitBtnText: BTN_TEXT_ENABLED,
                showAlert: true,
                alertMsg,
                alertType: 'danger',
            })
        }
    }

    onPurchaseSubmitHandler = async () => {
        //Remove any alerts that might be displayed and disable submit button
        this.props.updateAlertAndBtnState({
            disableSubmit: true,
            submitBtnText: BTN_TEXT_PROCESSING,
            showAlert: false,
            alertMsg: '',
            alertType: 'danger',
        })

        let payload = await this.state.paymentInstance.requestPaymentMethod()

        let data = {
            nonce: payload.nonce,
            ...(payload.deviceData && { deviceData: payload.deviceData }),
            passTierId: _.get(this.props, 'passTier._id'),
            showId: _.get(this.props, 'show._id'),
            ...(!this.props.isAuthenticated &&
                this.state.customFieldsValid && { userEmail: this.state.customFieldEmailValue }),
            countryOfIssuance: getPaymentCountryCode(payload),
        }

        this.props.purchaseNewPass(data)
    }

    hasCustomError() {
        //Custom fields are only relevant for non-users
        return !this.props.isAuthenticated && !this.state.customFieldsValid
    }

    handleCustomFieldsValidChange = (valid, value) => {
        this.setState({ customFieldsValid: valid, customFieldEmailValue: value })
    }

    renderPaywallContent = () => {
        const { classes, show, displayState } = this.props
        const totalAmount = this.props.passTier.amount + this.props.passTier.serviceFee

        if (displayState === PaywallDisplay.PAYMENT) {
            return (
                <div>
                    <h2 className={classes.paywallHeading}>register here</h2>
                    <div className={classes.pass}>
                        <img className={classes.passImg} src={show.posterImg} alt="" />
                        <div className={classes.passContent}>
                            <h3 className={classes.passTitle}>{show.title}</h3>
                        </div>
                    </div>
                    <div className={classes.paymentInfo}>
                        <div className={classes.feeBreakdown}>
                            <h3 className={classes.total}>Total</h3>
                            <span className={classes.passAmount}>{numeral(totalAmount).format('$0,0.00')}</span>
                        </div>
                        <div className={clsx(classes.feeBreakdown)}>
                            <h6 className={classes.description}>Pass</h6>
                            <span className={classes.descriptionAmount}>
                                {numeral(this.props.passTier.amount).format('$0,0.00')}
                            </span>
                        </div>
                        <hr className={classes.descriptionLine} />
                        <div className={clsx(classes.feeBreakdown)}>
                            <h6 className={classes.description}>Fulfillment Fee</h6>
                            <span className={classes.descriptionAmount}>
                                {numeral(this.props.passTier.serviceFee).format('$0,0.00')}
                            </span>
                        </div>
                        <hr className={classes.descriptionLine} />
                    </div>
                </div>
            )
        } else {
            return <p className={classes.loading}>Loading...</p>
        }
    }

    resetPaymentValues = () => {
        this.props.paymentTokenReceived('')
        this.props.updateAlertAndBtnState({
            disableSubmit: true,
            submitBtnText: BTN_TEXT_ENABLED,
            showAlert: false,
            alertMsg: '',
            alertType: 'danger',
        })
    }

    /**
     * It's important that once the #dropin-container div is rendered that it is never re-rendered between state
     * changes or else Braintree errors will occurr
     */
    render = () => {
        const { classes } = this.props

        return (
            <PaywallContainer goBackStep={PaywallDisplay.CHOOSE_PASS_TIER} onGoBack={this.resetPaymentValues}>
                <Alert
                    show={this.props.showAlert}
                    onClose={() => {
                        this.props.updateAlertAndBtnState({
                            disableSubmit: this.props.disableSubmit,
                            submitBtnText: BTN_TEXT_ENABLED,
                            showAlert: false,
                            alertMsg: '',
                            alertType: 'danger',
                        })
                    }}
                    dismissible
                    variant={this.props.alertType}
                    className="text-center"
                >
                    <span
                        className={clsx('body-3', {
                            'text-pink': this.props.alertType === 'danger',
                        })}
                    >
                        {_.isString(this.props.alertMsg) ? this.props.alertMsg : 'Error occurred. Please try again'}
                    </span>
                </Alert>
                {this.renderPaywallContent()}
                <div id="dropin-container" />
                {/* Ensure the #dropin-container is never re-rendered between state changes*/}
                {this.props.displayState === PaywallDisplay.PAYMENT && (
                    <>
                        {!isMobile() && (
                            <p className={classes.paywallSubheading}>Please note: we accept Venmo on mobile</p>
                        )}
                        {!this.props.isAuthenticated && (
                            <PaywallEmailInput
                                validationSchema={validationSchema}
                                onValidChange={this.handleCustomFieldsValidChange}
                            />
                        )}
                        <CallToActionButton
                            onClick={this.onPurchaseSubmitHandler}
                            display="block"
                            type="secondary"
                            disabled={
                                this.props.disableSubmit ||
                                this.hasCustomError() ||
                                (_.isFunction(this.state.paymentInstance.isPaymentMethodRequestable) &&
                                    !this.state.paymentInstance.isPaymentMethodRequestable())
                            }
                            text={this.props.submitBtnText || BTN_TEXT_ENABLED}
                            size={isMobile() ? 'normal' : 'large'}
                            className={clsx(classes.submitPassBtn, classes.paywallSubmitBtn)}
                            id="paywall-submit-btn"
                        />
                    </>
                )}
            </PaywallContainer>
        )
    }
}

Paywall.propTypes = {
    remainingPasses: PropTypes.number,
    passTier: PropTypes.object.isRequired,
    isAuthenticated: PropTypes.bool,
    successfulPurchaseCallback: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
    displayState: getDisplayState(state),
    paymentToken: getPaymentToken(state),
    show: getShow(state),
    disableSubmit: getSubmitBtnState(state),
    showAlert: getAlertState(state),
    alertType: getAlertType(state),
    alertMsg: getAlertMsg(state),
    submitBtnText: getSubmitBtnTxt(state),
    shouldClearSelectedPayment: shouldClearSelectedPayment(state),
    passTier: getChosenPassTier(state),
})

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            retrievePaymentToken,
            setDisplayState,
            purchaseNewPass,
            successfulPurchaseCallback,
            openModal,
            updateAlertAndBtnState,
            paymentTokenReceived,
        },
        dispatch,
    )

export default compose(connect(mapStateToProps, mapDispatchToProps), withStyles(styles))(Paywall)
